import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { assetImage } from "@/lib/media";
import { useTranslation } from "react-i18next";

/**
 * Horizontal thumbnail strip. Shows all screenshots when they fit; when they
 * overflow (e.g. on smaller widths), it becomes a draggable slider with
 * pagination arrows that only appear while there is more to scroll.
 */
const StripCarousel = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const [api, setApi] = useState<CarouselApi>();
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (!api) return;
    const update = () => {
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };
    update();
    api.on("select", update);
    api.on("reInit", update);
    return () => {
      api.off("select", update);
      api.off("reInit", update);
    };
  }, [api]);

  const scrollable = canPrev || canNext;

  return (
    <Carousel
      setApi={setApi}
      opts={{ align: "start", dragFree: true, containScroll: "trimSnaps" }}
      className={cn("w-full", className)}
    >
      <CarouselContent className="-ml-2.5">{children}</CarouselContent>
      {scrollable && (
        <>
          <CarouselPrevious className="left-1 h-8 w-8 border-accent/40 bg-background/80 text-accent backdrop-blur-sm hover:bg-accent hover:text-accent-foreground" />
          <CarouselNext className="right-1 h-8 w-8 border-accent/40 bg-background/80 text-accent backdrop-blur-sm hover:bg-accent hover:text-accent-foreground" />
        </>
      )}
    </Carousel>
  );
};

export type LightboxImage = { src: string; alt: string };

interface ScreenshotLightboxProps {
  images: LightboxImage[];
  /** "strip" = compact horizontal thumbnails (card); "grid" = responsive gallery. */
  layout?: "strip" | "grid";
  className?: string;
}

/**
 * Clickable screenshot thumbnails that open a full-screen, swipeable lightbox.
 * Reused on the homepage showcase card (strip) and the case-study page (grid).
 */
const ScreenshotLightbox = ({ images, layout = "grid", className }: ScreenshotLightboxProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const openAt = (index: number) => {
    setStartIndex(index);
    setCurrent(index);
    setOpen(true);
  };

  // Keep the counter in sync as the user slides.
  const onApi = (a: CarouselApi) => {
    setApi(a);
    if (!a) return;
    a.on("select", () => setCurrent(a.selectedScrollSnap()));
  };

  if (!images.length) return null;

  const Thumb = ({ img, i }: { img: LightboxImage; i: number }) => {
    // UI screenshots contain small typography; JPEG artifacts are immediately
    // visible here, so thumbnails stay PNG and only resize dimensionally.
    const thumb = assetImage(img.src, layout === "strip" ? 320 : 720, {
      jpeg: false,
      quality: 92,
    });
    return (
    <DialogTrigger asChild>
      <button
        type="button"
        onClick={() => openAt(i)}
        aria-label={img.alt}
        className={cn(
          "group/thumb relative overflow-hidden rounded-xl border border-border bg-card transition-[border-color,transform] duration-200 hover:border-accent/50 focus-visible:border-accent/50",
          layout === "strip" ? "h-16 w-full" : "aspect-[16/10] w-full",
        )}
      >
        <img
          src={thumb.src}
          srcSet={thumb.srcSet}
          sizes={layout === "strip" ? "240px" : "(max-width: 768px) 50vw, 520px"}
          alt={img.alt}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover object-top transition-transform duration-300 group-hover/thumb:scale-[1.04]"
        />
        <span className="absolute inset-0 bg-background/30 opacity-0 transition-opacity duration-200 group-hover/thumb:opacity-100" />
        <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md bg-background/70 text-accent opacity-0 backdrop-blur-sm transition-opacity duration-200 group-hover/thumb:opacity-100">

          <Maximize2 size={14} />
        </span>
      </button>
    </DialogTrigger>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Thumbnails */}
      {layout === "strip" ? (
        <StripCarousel className={className}>
          {images.map((img, i) => (
            <CarouselItem key={img.src} className="basis-1/3 pl-2.5 sm:basis-1/4 lg:basis-1/5">
              <Thumb img={img} i={i} />
            </CarouselItem>
          ))}
        </StripCarousel>
      ) : (
        <div className={cn("grid grid-cols-2 md:grid-cols-3 gap-4", className)}>
          {images.map((img, i) => (
            <Thumb key={img.src} img={img} i={i} />
          ))}
        </div>
      )}

      <DialogContent className="left-0 right-0 top-16 lg:top-20 bottom-0 flex h-[calc(100dvh-4rem)] w-screen max-w-none translate-x-0 translate-y-0 flex-col justify-center gap-0 rounded-none border-0 border-t border-border bg-card/95 p-4 backdrop-blur-xl data-[state=closed]:slide-out-to-top-0 data-[state=open]:slide-in-from-top-0 sm:p-6 lg:h-[calc(100dvh-5rem)]">
        <DialogTitle className="sr-only">{t("caseStudy.galleryTitle", "Screenshots")}</DialogTitle>
        <Carousel
          opts={{ startIndex, loop: true }}
          setApi={onApi}
          className="w-full px-10"
        >
          <CarouselContent>
            {images.map((img) => (
              <CarouselItem key={img.src} className="flex flex-col items-center justify-center">
                <div className="flex items-center justify-center overflow-hidden rounded-xl border border-border bg-background">
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    decoding="async"
                    className="max-h-[calc(100dvh-13rem)] w-auto max-w-full object-contain"
                  />

                </div>
                <p className="mt-3 text-center text-sm text-muted-foreground">{img.alt}</p>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-0" />
          <CarouselNext className="right-0" />
        </Carousel>
        <p className="mt-2 text-center font-mono text-xs tracking-[0.15em] text-muted-foreground/70">
          {current + 1} / {images.length}
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default ScreenshotLightbox;
